import React, { useState } from 'react';
import { PhoneIcon, CheckIcon, ThumbsUpIcon, MessageCircleIcon, Share2Icon, PaperclipIcon, PinIcon } from 'lucide-react';
import { Maximize2Icon, Minimize2Icon, XIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCity } from '../../contexts/CityContext';
import { useEmailIdentity } from '../../contexts/EmailIdentityContext';
import { IconTooltip } from '../IconTooltip';
import { EmailTemplate } from './EmailTemplate';
import { hasContent } from '../RichTextEditor';
import { resolveHtml, resolveText } from '../../utils/variables';
import type { NoticeVariable } from '../../types/variables';
import type { ChannelsState } from '../../types/composer';
export type PreviewTab = 'push' | 'sms' | 'email' | 'voice' | 'website' | 'social' | 'screens' | 'app';
interface NoticePreviewProps {
  channels: ChannelsState;
  title: string;
  description: string;
  categoryLabel: string;
  previewTab: PreviewTab;
  onPreviewTabChange: (tab: PreviewTab) => void;
  /** Variables used to resolve chips into real values. */
  variables: NoticeVariable[];
  /** Language the notice content is written in. */
  contentLang: 'fr' | 'en';
  /** Mock-ups render larger (wide preview panel). */
  expanded?: boolean;
  /** Rendered as a full-height side panel with its own header (split screen). */
  docked?: boolean;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  onClose?: () => void;
}
export function NoticePreview({
  channels,
  title,
  description,
  categoryLabel,
  previewTab,
  onPreviewTabChange,
  variables,
  contentLang,
  expanded = false,
  docked = false,
  fullscreen = false,
  onToggleFullscreen,
  onClose
}: NoticePreviewProps) {
  const {
    t
  } = useLanguage();
  const {
    city
  } = useCity();
  const {
    identity
  } = useEmailIdentity();
  const [voiceTestPhone, setVoiceTestPhone] = useState('');
  const [voiceTestSent, setVoiceTestSent] = useState(false);
  /** App preview: the feed card (summary) or the opened notice (full content). */
  const [appView, setAppView] = useState<'feed' | 'detail'>('feed');

  /** Plain text with every variable replaced by its value. */
  const text = (html: string) => resolveText(html, variables, contentLang);
  /** Rich HTML with every variable replaced by its value. */
  const rich = (html: string) => resolveHtml(html, variables, contentLang);
  const tabs: {
    id: PreviewTab;
    label: string;
  }[] = [{
    id: 'app',
    label: t('App', 'App')
  }, {
    id: 'push',
    label: t('Push', 'Push')
  }, {
    id: 'sms',
    label: t('SMS', 'SMS')
  }, {
    id: 'email',
    label: t('Courriel', 'Email')
  }, {
    id: 'voice',
    label: t('Voix', 'Voice')
  }, {
    id: 'website',
    label: t('Site web', 'Website')
  }, {
    id: 'social',
    label: t('Social', 'Social')
  }, {
    id: 'screens',
    label: t('Écrans', 'Screens')
  }];
  const isPhone = previewTab === 'push' || previewTab === 'sms' || previewTab === 'voice';
  const isDesktop = previewTab === 'email' || previewTab === 'website';
  const tabBar = <div className="flex flex-wrap gap-1 bg-slate-200/50 p-1 rounded-lg" data-id="element-4239" data-mp="239kmv1g1yi6s">
      {tabs.map(tab => <button key={tab.id} type="button" onClick={() => {
      onPreviewTabChange(tab.id);
      setVoiceTestSent(false);
    }} aria-pressed={previewTab === tab.id} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${previewTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4240" data-mp="1ycu8cijwbunh">
          {tab.label}
        </button>)}
    </div>;
  const renderMockups = (expanded: boolean) => <div className="flex justify-center" style={expanded && !isDesktop ? {
    zoom: 1.3
  } : undefined} data-id="element-4241" data-mp="15nwpad7v1tto">
        {/* Phone mockup — push / sms / voice */}
        {isPhone && <div className="w-[300px] h-[600px] bg-white rounded-[40px] border-[8px] border-slate-900 overflow-hidden relative" data-id="element-4242" data-mp="wn89pe2tjleo">
            <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20" data-id="element-4243" data-mp="2wp85schzh78a">
              <div className="w-32 h-6 bg-slate-900 rounded-b-xl" data-id="element-4244" data-mp="30oqgygk063xx" />
            </div>

            <div className="w-full h-full bg-slate-50 pt-12 px-4 relative" data-id="element-4245" data-mp="2mxka1zee9410">
              {previewTab === 'push' && <div className="bg-white/80 backdrop-blur-md rounded-2xl p-4 border border-slate-100 mt-4" data-id="element-4246" data-mp="2rw5anzxk5ppz">
                  <div className="flex items-center gap-2 mb-2" data-id="element-4247" data-mp="2cz4gzqvt3kov">
                    <img src="https://cdn.magicpatterns.com/uploads/ckK2136PdZbnMQPqZiNST7/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="w-5 h-5 rounded object-cover flex-shrink-0" data-id="element-4248" data-mp="2i0lerdizur18" />
                    <span className="text-xs font-medium text-slate-500 truncate" data-id="element-4249" data-mp="29fqje7tx578">
                      {t('Ville de Windsor', 'City of Windsor')}
                    </span>
                    <span className="text-xs text-slate-400 ml-auto" data-id="element-4250" data-mp="hawa4buorcyz">
                      {t('Maintenant', 'Now')}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-1" data-id="element-4251" data-mp="cai0wqhclwzd">
                    {text(channels.push.title) || title || t('Titre de la notification', 'Notification title')}
                  </h4>
                  <p className="text-sm text-slate-600 leading-snug" data-id="element-4252" data-mp="100ujqwe4te4q">
                    {text(channels.push.message) || description || t('Le message de votre notification apparaîtra ici…', 'Your notification message will appear here…')}
                  </p>
                </div>}

              {previewTab === 'sms' && <div className="mt-8 space-y-4" data-id="element-4253" data-mp="36sd7cc4e5y6q">
                  <div className="flex justify-center" data-id="element-4254" data-mp="36toi4vw9867c">
                    <span className="text-[10px] font-medium text-slate-400" data-id="element-4255" data-mp="19rz7n98dlvjm">
                      {t("Aujourd'hui 10:42", 'Today 10:42')}
                    </span>
                  </div>
                  <div className="bg-blue-500 text-white rounded-2xl rounded-tr-sm p-3 max-w-[85%] ml-auto" data-id="element-4256" data-mp="1jpwubsal7ka4">
                    <p className="text-sm leading-snug" data-id="element-4257" data-mp="1ooqbbup31tf1">
                      {text(channels.sms.text) || t('Votre message texte apparaîtra ici. Il doit être court et direct.', 'Your text message will appear here. Keep it short and direct.')}
                    </p>
                  </div>
                </div>}

              {previewTab === 'voice' && <div className="absolute inset-0 bg-[#0F172A] pt-14 px-5 flex flex-col" data-id="element-4258" data-mp="27hbylqlxcbo0">
                  <div className="flex flex-col items-center text-center" data-id="element-4259" data-mp="2cchm2tuz5ye7">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-widest mb-4" data-id="element-4260" data-mp="2ggpasr1fnecf">
                      {t('Appel entrant', 'Incoming call')}
                    </span>
                    <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center mb-3" data-id="element-4261" data-mp="qfqym0ptil0">
                      <PhoneIcon className="w-8 h-8 text-[#4F46E5]" data-id="element-4262" data-mp="3ry1y4omn836m" />
                    </div>
                    <h4 className="text-base font-semibold text-white" data-id="element-4263" data-mp="3n005pd3burih">
                      {t('Ville de Windsor', 'City of Windsor')}
                    </h4>
                    <p className="text-xs text-slate-400 mb-4" data-id="element-4264" data-mp="jh6wfchl0ou4">
                      {t('Message vocal automatisé', 'Automated voice message')}
                    </p>
                  </div>

                  <div className="bg-white/10 rounded-2xl p-3 text-left mb-4 overflow-y-auto max-h-32" data-id="element-4265" data-mp="9s6xft32xtfx">
                    <p className="text-xs text-slate-200 leading-relaxed" data-id="element-4266" data-mp="177t4v4vi0b4n">
                      {text(channels.voice.script) || t('Le script de votre appel vocal sera lu ici par une voix automatisée…', 'Your call script will be read here by an automated voice…')}
                    </p>
                  </div>

                  <div className="mt-auto bg-white rounded-2xl p-4 mb-4" data-id="element-4267" data-mp="1tcuo36qw933">
                    {voiceTestSent ? <div className="text-center" data-id="element-4268" data-mp="5upy66md806u">
                        <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2" data-id="element-4269" data-mp="tjr33jvzot7d">
                          <CheckIcon className="w-5 h-5 text-emerald-600" data-id="element-4270" data-mp="yiua39vj59hz" />
                        </div>
                        <p className="text-sm font-semibold text-slate-900" data-id="element-4271" data-mp="khrlapd5hl3d">
                          {t('Test envoyé !', 'Test sent!')}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-4272" data-mp="359bkaehawuz3">
                          {t('Un appel test est en route vers ', 'A test call is on its way to ')}
                          {voiceTestPhone}.
                        </p>
                        <button onClick={() => setVoiceTestSent(false)} className="mt-2 text-xs font-semibold text-[#4F46E5] hover:text-[#4338CA]" data-id="element-4273" data-mp="2xqao6e8ty3lr">
                          {t('Envoyer un autre test', 'Send another test')}
                        </button>
                      </div> : <>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5" data-id="element-4274" data-mp="2e2l2morhkzkz">
                          {t("Tester l'appel vocal", 'Test the voice call')}
                        </label>
                        <input type="tel" value={voiceTestPhone} onChange={e => setVoiceTestPhone(e.target.value)} placeholder="(514) 555-0199" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] mb-2" data-id="element-4275" data-mp="2nyoe4l88aakl" />
                        <button onClick={() => {
                if (voiceTestPhone.trim()) setVoiceTestSent(true);
              }} disabled={!voiceTestPhone.trim()} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-4276" data-mp="coiklsmczee8">
                          <PhoneIcon className="w-4 h-4" data-id="element-4277" data-mp="3l4kgq74769a6" />{' '}
                          {t('Envoyer un test', 'Send a test')}
                        </button>
                      </>}
                  </div>
                </div>}
            </div>
          </div>}

        {/* Social post mockup */}
        {previewTab === 'social' && <div className="w-full max-w-[360px] bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-4278" data-mp="1rnxxzmzuyb3w">
            <div className="flex items-center gap-3 p-3 border-b border-slate-100" data-id="element-4279" data-mp="20mjfdetlfk33">
              <img src="https://cdn.magicpatterns.com/uploads/kfqfiE7nmccz961Hy3x3v1/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-4280" data-mp="25lcsv78vrb39" />
              <div className="min-w-0" data-id="element-4281" data-mp="1cwywsx1cqouc">
                <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-4282" data-mp="1hva3iywcoj8c">
                  {t('Ville de Windsor', 'City of Windsor')}
                </p>
                <p className="text-[11px] text-slate-400" data-id="element-4283" data-mp="1moldh8wglg4h">
                  {t("À l'instant · ", 'Just now · ')}
                  <span aria-hidden="true" data-id="element-4284" data-mp="p5a5nk09o6ez">🌐</span>
                </p>
              </div>
            </div>

            <div className="px-3 py-2" data-id="element-4285" data-mp="m5i41o4veal4">
              {hasContent(channels.social.postText) ? <div className="text-sm text-slate-700 leading-snug [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-2" dangerouslySetInnerHTML={{
          __html: rich(channels.social.postText)
        }} data-id="element-4286" data-mp="i3wk5itx4w1f" /> : <p className="text-sm text-slate-400" data-id="element-4287" data-mp="36xh88a0hrda">
                  {t('Le texte de votre publication apparaîtra ici.', 'Your post text will appear here.')}
                </p>}
            </div>

            <div className="flex items-center justify-around px-3 py-2 border-t border-slate-100 text-slate-500" data-id="element-4288" data-mp="84z0oubcnspa">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-4289" data-mp="1oifh4k4wclwe">
                <ThumbsUpIcon className="w-4 h-4" data-id="element-4290" data-mp="1jk44y2r4pzb0" /> {t("J'aime", 'Like')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-4291" data-mp="31hr1wikrx7f5">
                <MessageCircleIcon className="w-4 h-4" data-id="element-4292" data-mp="36iohqoib9pdh" /> {t('Commenter', 'Comment')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-4293" data-mp="2sivu5222zp9q">
                <Share2Icon className="w-4 h-4" data-id="element-4294" data-mp="2xg5i0d9tnsvg" /> {t('Partager', 'Share')}
              </span>
            </div>
          </div>}

        {/* Citizen app mockup — notice in the news feed */}
        {previewTab === 'app' && <div className="flex flex-col items-center gap-3" data-id="element-4295" data-mp="3p7bl92kua6oy">
          <div role="tablist" aria-label={t('Vue de l’application', 'App view')} className="flex gap-1 bg-slate-200/50 p-1 rounded-lg" data-id="element-4296" data-mp="3bhhwek9c3mf8">
            {([['feed', t('Fil', 'Feed')], ['detail', t('Avis ouvert', 'Opened notice')]] as const).map(([id, label]) => <button key={id} role="tab" aria-selected={appView === id} onClick={() => setAppView(id)} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${appView === id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4297" data-mp="2ka13z9hsvfm8">
                {label}
              </button>)}
          </div>
          <div className="w-[300px] h-[600px] bg-white rounded-[40px] border-[8px] border-slate-900 overflow-hidden relative" data-id="element-4298" data-mp="2z08f145h9wcw">
            <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20" data-id="element-4299" data-mp="2u6mda70urcvv">
              <div className="w-32 h-6 bg-slate-900 rounded-b-xl" data-id="element-4300" data-mp="2p8kuzob93hlg" />
            </div>
            <div className="w-full h-full bg-slate-50 flex flex-col" data-id="element-4301" data-mp="3mptb8oqn6q44">
              <div className="bg-white border-b border-slate-100 pt-9 pb-3 px-4 flex items-center gap-2 flex-shrink-0" data-id="element-4302" data-mp="3hyb93xcvujhk">
                <img src="https://cdn.magicpatterns.com/uploads/ckK2136PdZbnMQPqZiNST7/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="w-7 h-7 rounded-full object-cover flex-shrink-0" data-id="element-4303" data-mp="3p9zhj32qasnu" />
                <div className="min-w-0" data-id="element-4304" data-mp="742x3szqmito">
                  <p className="text-sm font-semibold text-slate-900 leading-tight" data-id="element-4305" data-mp="754gqlutkdn0">
                    {t('Ville de Windsor', 'City of Windsor')}
                  </p>
                  <p className="text-[11px] text-slate-500 leading-tight" data-id="element-4306" data-mp="h0pfmb4lcxsj">
                    {t('Fil d’actualité', 'News feed')}
                  </p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-3" data-id="element-4307" data-mp="qrtw5f9x0qph">
                <article className="bg-white rounded-2xl border border-slate-200 p-4" data-id="element-4308" data-mp="ut5pnvvwtw2y">
                  <div className="flex items-center gap-2 mb-2 flex-wrap" data-id="element-4309" data-mp="urb209t97pv6">
                    <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-[#4F46E5] text-[10px] font-semibold" data-id="element-4310" data-mp="zsi9s8vecp1u">
                      {categoryLabel}
                    </span>
                    {channels.app.pinned && <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500" data-id="element-4311" data-mp="35l6kgva4t179">
                        <PinIcon className="w-3 h-3" data-id="element-4312" data-mp="30uq4p4iu93hq" />
                        {t('Épinglé', 'Pinned')}
                      </span>}
                    <span className="text-[10px] text-slate-400 ml-auto" data-id="element-4313" data-mp="3fhjbfleal0yb">
                      {t('À l’instant', 'Just now')}
                    </span>
                  </div>
                  <h4 className="text-[15px] font-semibold text-slate-900 leading-snug" data-id="element-4314" data-mp="3oizj3ldq2oyu">
                    {text(channels.app.title) || title || t('Titre de l’avis', 'Notice title')}
                  </h4>
                  {appView === 'feed' ? <>
                      <p className={`mt-1.5 text-[13px] leading-relaxed line-clamp-3 ${hasContent(channels.app.summary) ? 'text-slate-600' : 'text-slate-400'}`} data-id="element-4315" data-mp="1p8rg9d9c70jg">
                        {text(channels.app.summary) || text(channels.app.body) || t('Le résumé de votre avis apparaîtra ici…', 'Your notice summary will appear here…')}
                      </p>
                      <button type="button" onClick={() => setAppView('detail')} className="mt-2 text-xs font-semibold text-[#4F46E5] hover:underline" data-id="element-4316" data-mp="3n4gdycobzk1k">
                        {t('Lire la suite', 'Read more')}
                      </button>
                    </> : hasContent(channels.app.body) ? <div className="mt-2 text-[13px] text-slate-600 leading-relaxed space-y-2 [&_img]:rounded-lg [&_img]:my-2 [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4" dangerouslySetInnerHTML={{
                __html: rich(channels.app.body)
              }} data-id="element-4317" data-mp="1bb8fbhaummbh" /> : <p className="mt-2 text-[13px] text-slate-400 leading-relaxed" data-id="element-4318" data-mp="3gjpeow2a7x49">
                      {description || t('Le contenu de votre avis apparaîtra ici…', 'Your notice content will appear here…')}
                    </p>}
                </article>

                {/* Older posts, dimmed for context */}
                {appView === 'feed' && [0, 1].map(i => <div key={i} aria-hidden="true" className="bg-white rounded-2xl border border-slate-100 p-4 opacity-50" data-id="element-4319" data-mp="10s1jb52x2cnk">
                    <div className="h-2.5 w-16 rounded-full bg-slate-200 mb-3" data-id="element-4320" data-mp="14aqz24q25pv8" />
                    <div className="h-3 w-4/5 rounded-full bg-slate-200 mb-2" data-id="element-4321" data-mp="18bsqhzdqgxmn" />
                    <div className="h-2.5 w-full rounded-full bg-slate-100 mb-1.5" data-id="element-4322" data-mp="1d32ekvjmvswf" />
                    <div className="h-2.5 w-2/3 rounded-full bg-slate-100" data-id="element-4323" data-mp="1i2dxaa3k98gn" />
                  </div>)}
              </div>
            </div>
          </div>
          </div>}

        {/* Public screen mockup — 16:9 landscape signage */}
        {previewTab === 'screens' && <div className="w-full max-w-[400px]" data-id="element-4324" data-mp="udcj0681r33g">
            <div className="rounded-xl bg-slate-900 p-2.5 shadow-sm" data-id="element-4325" data-mp="ubhxoqsv58yu">
              <div className="aspect-video rounded-lg bg-[#0F172A] overflow-hidden flex flex-col" data-id="element-4326" data-mp="zcfofrrxvbby">
                <div className="flex items-center justify-between px-4 pt-3.5 flex-shrink-0" data-id="element-4327" data-mp="25t14su9kdw2o">
                  <img src="https://cdn.magicpatterns.com/uploads/kfqfiE7nmccz961Hy3x3v1/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="h-6 w-6 rounded object-cover bg-white" data-id="element-4328" data-mp="2aqjisz2fv1hj" />
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-4329" data-mp="6thg21gzyo0">
                    {categoryLabel}
                  </span>
                </div>

                <div className="flex-1 flex flex-col justify-center px-5 py-3 min-h-0" data-id="element-4330" data-mp="33na61hq2pcvm">
                  {hasContent(channels.screens.headline) ? <p className="text-lg font-bold text-white leading-tight line-clamp-2" data-id="element-4331" data-mp="38n6bzmibzedt">
                      {text(channels.screens.headline)}
                    </p> : <p className="text-lg font-bold text-slate-600 leading-tight" data-id="element-4332" data-mp="3hnumi78imwly">
                      {title || t('Avis important', 'Important notice')}
                    </p>}
                  {hasContent(channels.screens.bodyText) ? <p className="mt-2 text-[11px] text-slate-300 leading-snug line-clamp-4" data-id="element-4333" data-mp="2hemt4x0o9qz3">
                      {text(channels.screens.bodyText)}
                    </p> : <p className="mt-2 text-[11px] text-slate-500 leading-snug" data-id="element-4334" data-mp="28mb3hdddu7k6">
                      {t('Votre message apparaîtra ici, en gros caractères.', 'Your message will appear here, in large type.')}
                    </p>}
                </div>

                <div className="px-4 pb-3 flex items-center justify-between flex-shrink-0" data-id="element-4335" data-mp="1tnqy7r0x6fnm">
                  <span className="text-[9px] text-slate-500" data-id="element-4336" data-mp="1po8ntp6q5mz1">
                    {t('windsor.ca', 'windsor.ca')}
                  </span>
                  {/* Slide progress bar */}
                  <span className="h-0.5 w-20 rounded-full bg-slate-700 overflow-hidden" data-id="element-4337" data-mp="3k0orci84yn07">
                    <span className="block h-full w-2/3 bg-[#4F46E5]" data-id="element-4338" data-mp="3f2ddlwepzscp" />
                  </span>
                </div>
              </div>
            </div>

            <p className="mt-2.5 text-xs text-slate-500 text-center" data-id="element-4339" data-mp="2019febw3zmq">
              {t(`Rotation de ${channels.screens.duration} s sur ${channels.screens.locations.length} écran${channels.screens.locations.length > 1 ? 's' : ''}`, `${channels.screens.duration} s rotation on ${channels.screens.locations.length} screen${channels.screens.locations.length > 1 ? 's' : ''}`)}
            </p>
          </div>}

        {/* Desktop mockup — email / website */}
        {(previewTab === 'email' || previewTab === 'website') && <div className={`w-full ${expanded ? 'max-w-[820px]' : 'max-w-[360px]'}`} data-id="element-4340" data-mp="brfgc76681pw">
            <div className="bg-white rounded-xl border border-slate-300 overflow-hidden shadow-sm" data-id="element-4341" data-mp="gp754sbir7pv">
              <div className="h-9 bg-slate-100 border-b border-slate-200 flex items-center gap-1.5 px-3" data-id="element-4342" data-mp="2wgbjr5wz09u4">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-4343" data-mp="31iamcdkvz2q0" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" data-id="element-4344" data-mp="2twf7qrcyp0lw" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" data-id="element-4345" data-mp="2oywuwpvtq18m" />
                <div className="ml-2 flex-1 h-5 bg-white rounded border border-slate-200 text-[9px] text-slate-400 flex items-center px-2 truncate" data-id="element-4346" data-mp="2p0q0s2ym4hfk">
                  {previewTab === 'email' ? `mail.${identity.website}` : 'windsor.ca'}
                </div>
              </div>

              <div className={`bg-white ${expanded ? 'h-[72vh]' : 'h-[440px]'} overflow-y-auto relative`} data-id="element-4347" data-mp="2a9qx96cmwj2u">
                {previewTab === 'email' && <div className="bg-white" data-id="element-4348" data-mp="2jzk8g68v6w6k">
                    <div className={`${expanded ? 'px-8' : 'px-5'} py-3 border-b border-slate-100`} data-id="element-4349" data-mp="2k0lokp4qm8v5">
                      <p className="text-xs text-slate-500 mb-1" data-id="element-4350" data-mp="2f6hc5qcpd8mo">
                        {contentLang === 'fr' ? `De : Ville de ${city}` : `From: City of ${city}`}
                      </p>
                      <h4 className="text-sm font-semibold text-slate-900" data-id="element-4351" data-mp="1mjmwd6qtyxfj">
                        {text(channels.email.subject) || title || t('Objet du courriel', 'Email subject')}
                      </h4>
                    </div>
                    <EmailTemplate identity={identity} cityName={city} title={title || t("Titre de l'avis", 'Notice title')} bodyHtml={hasContent(channels.email.body) ? rich(channels.email.body) : null} placeholder={description || t("Le contenu détaillé de votre courriel s'affichera ici.", 'The detailed content of your email will appear here.')} attachments={channels.email.attachments} lang={contentLang} large={expanded} data-id="element-4352" data-mp="2a38gv4j6i1da" />
                  </div>}

                {previewTab === 'website' && <div className="relative" data-id="element-4353" data-mp="3bze5t5k1wu89">
                    <img src="https://cdn.magicpatterns.com/uploads/paDPkCcwNHTw4rcW21cM4q/image.png" alt={t('Aperçu du site web de Windsor', 'Preview of the Windsor website')} className="w-full block" data-id="element-4354" data-mp="3bymngwfgsszx" />
                    <div className="absolute top-[13%] inset-x-0 bg-[#E7F1FB] px-3 py-3 border-y border-[#cfe0f5]" data-id="element-4355" data-mp="3vnn6k3znu8jd">
                      <div className="flex items-start justify-between gap-2" data-id="element-4356" data-mp="3qniowan0n7rk">
                        <div className="min-w-0" data-id="element-4357" data-mp="3lqijm1xzq91i">
                          <span className="inline-block px-3 py-0.5 rounded-full bg-[#1D4ED8] text-white text-[10px] font-bold mb-1.5" data-id="element-4358" data-mp="3lrtrodygy2yf">
                            {categoryLabel}
                          </span>
                          <p className="text-[11px] font-bold text-slate-900 leading-snug" data-id="element-4359" data-mp="2brpvftp90dqv">
                            {text(channels.website.bannerText) || title || t('Le texte de votre bannière apparaîtra ici', 'Your banner text will appear here')}
                          </p>
                          <p className="text-[10px] font-semibold text-slate-800 underline mt-1" data-id="element-4360" data-mp="1t06tlq6yn5qf">
                            {text(channels.website.ctaLabel) || t("Consulter l'avis en cours", 'View the current notice')}
                          </p>
                        </div>
                        <span className="text-slate-500 text-sm leading-none" data-id="element-4361" data-mp="1o2ybfpltb1p6">✕</span>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            <div className="mx-auto h-2.5 w-[88%] bg-slate-300 rounded-b-xl" data-id="element-4362" data-mp="1f0p04xipz4aq" />
            <div className="mx-auto h-1 w-[40%] bg-slate-200 rounded-b-lg" data-id="element-4363" data-mp="vggd155vr1zq" />
          </div>}
      </div>;
  const iconButton = 'p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]';
  if (docked) {
    return <section className="h-full flex flex-col bg-white" aria-label={t('Aperçu', 'Preview')} data-id="element-4364" data-mp="10guokx6owu2i">
        <header className="shrink-0 h-12 px-4 flex items-center gap-3 border-b border-slate-200" data-id="element-4365" data-mp="1ecg84ou5mlnj">
          <h2 className="text-sm font-semibold text-slate-900 shrink-0" data-id="element-4366" data-mp="19dddzzfppzuc">{t('Aperçu', 'Preview')}</h2>
          <div className="flex-1 min-w-0 overflow-x-auto" data-id="element-4367" data-mp="1o6f6dcr3xla7">{tabBar}</div>
          <div className="flex items-center gap-0.5 shrink-0" data-id="element-4368" data-mp="1j6keb5vlipnk">
            {onToggleFullscreen && <IconTooltip label={fullscreen ? t('Quitter le plein écran', 'Exit full screen') : t('Plein écran', 'Full screen')} data-id="element-4369" data-mp="1x5ttq3bz9fqh">
                <button type="button" onClick={onToggleFullscreen} aria-pressed={fullscreen} aria-label={fullscreen ? t('Quitter le plein écran', 'Exit full screen') : t('Plein écran', 'Full screen')} className={iconButton} data-id="element-4370" data-mp="1t1m483xp3vr7">
                  {fullscreen ? <Minimize2Icon className="w-4 h-4" data-id="element-4371" data-mp="3dh4qbyspxfk1" /> : <Maximize2Icon className="w-4 h-4" data-id="element-4372" data-mp="3dg20lu74tj5p" />}
                </button>
              </IconTooltip>}
            {onClose && <IconTooltip label={t('Fermer l’aperçu', 'Close preview')} data-id="element-4373" data-mp="2lqgn8h7hi03q">
                <button type="button" onClick={onClose} aria-label={t('Fermer l’aperçu', 'Close preview')} className={iconButton} data-id="element-4374" data-mp="2psljzot8bcul">
                  <XIcon className="w-4 h-4" data-id="element-4375" data-mp="2ujk3ex9ikr22" />
                </button>
              </IconTooltip>}
          </div>
        </header>
        <div className="flex-1 overflow-y-auto bg-[#F7F7F8] px-6 py-8" data-id="element-4376" data-mp="1xzbc2fchnvzr">{renderMockups(expanded)}</div>
      </section>;
  }
  return <section className="space-y-4" data-id="element-4377" data-mp="1uj88jsn6nmgn">
      <div className="flex items-center justify-between gap-2 flex-wrap" data-id="element-4378" data-mp="1ui6vxjqiqrwt">
        <h2 className="text-base font-semibold text-slate-900" data-id="element-4379" data-mp="1pk59qpxsqch4">{t('Aperçu', 'Preview')}</h2>
        {tabBar}
      </div>

      {renderMockups(expanded)}
    </section>;
}